/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";

import { Timezone } from "@/constants/dateTime";
import { capitalizeEachWord } from "@/helpers/string";
import { AnalysisDatasetSummary } from "@/types/analysisSummary";
import { DatasetDataType } from "@/types/dataset";
import { translateToUserTimezone } from "@/utils/date";

/**
 * Custom hook to generate columns, rows, donutChartData, and barChartData
 * from the summary data.
 */
export function useAnalysisSummaryVisualization(
  summaryData: AnalysisDatasetSummary | undefined,
  highestScoreGridId: string,
  timezone?: Timezone
) {
  // Memoized columns
  const columns = useMemo(() => {
    if (!summaryData) return [];
    return summaryData.columns.map((col) => ({
      id: col.name,
      accessorKey: col.name,
      header: col.name
    }));
  }, [summaryData]);

  // Memoized rows
  const rows = useMemo(() => {
    if (!summaryData) return [];

    return summaryData.rows
      .map((row) => {
        const rowObj: Record<string, any> = {};
        summaryData.columns.forEach((col, idx) => {
          let value = row[idx] || "null";
          if (col.name === "Label" && typeof value === "string") {
            value = capitalizeEachWord(value || "null", "_");
          }
          if (
            col.data_type === DatasetDataType.TYPE_DATE ||
            col.data_type === DatasetDataType.TYPE_DATETIME ||
            col.data_type === DatasetDataType.TYPE_TIME
          ) {
            value = translateToUserTimezone(
              timezone,
              col.data_type,
              value?.toString() || "null"
            );
          }
          rowObj[col.name] =
            typeof value === "number" ? value.toLocaleString() : value;
        });

        const nonLabelKeys = Object.keys(rowObj).filter(
          (key) => key !== "Label"
        );
        // Remove rows where all non-label keys are zero
        const allZero =
          nonLabelKeys.length > 0 &&
          nonLabelKeys.every((key) => rowObj[key] === "0" || rowObj[key] === 0);

        if (allZero) return {};
        return rowObj;
      })
      .filter((rowObj) => Object.keys(rowObj).length > 0); // Remove empty object
  }, [summaryData, timezone]);

  // Memoized donut chart data
  const donutChartData = useMemo(() => {
    if (!summaryData) return [];

    const labelIndex = 0;

    // Find the column index for the highest score grid ID
    const valueIndex = summaryData.columns.findIndex(
      (col) => col.name === highestScoreGridId
    );

    const hasSeries = valueIndex !== -1;

    return summaryData.rows.map((row) => {
      const rowObj: Record<string, any> = {};

      // The category label is always taken from the first column
      const rawLabel = row[labelIndex];
      const columnInfo = summaryData.columns[labelIndex];

      if (
        columnInfo.data_type === DatasetDataType.TYPE_DATE ||
        columnInfo.data_type === DatasetDataType.TYPE_DATETIME ||
        columnInfo.data_type === DatasetDataType.TYPE_TIME
      ) {
        rowObj.name = translateToUserTimezone(
          timezone,
          columnInfo.data_type,
          rawLabel?.toString() || "null"
        );
      } else if (typeof rawLabel === "string") {
        rowObj.name = capitalizeEachWord(rawLabel || "null", "_");
      } else if (typeof rawLabel === "number") {
        rowObj.name = rawLabel.toLocaleString();
      } else {
        rowObj.name = "null";
      }

      // Value selection logic:
      // Series mode  : use the value from the highest score grid column
      // Non-series   : use the value from the second column
      if (hasSeries) {
        rowObj.value = Number(row[valueIndex]) || 0;
      } else {
        rowObj.value = Number(row[1]) || 0;
      }

      return rowObj;
    });
  }, [summaryData, highestScoreGridId, timezone]);

  // Memoized bar chart data
  const barChartData = useMemo(() => {
    if (!summaryData) return [];
    const labelIndex = 0;
    const valueIndexes = summaryData.columns
      .map((_, idx) => idx)
      .filter((idx) => idx !== labelIndex);

    return summaryData.rows
      .map((row) => {
        let label = row[labelIndex] ?? "";
        const columnInfo = summaryData.columns[labelIndex];

        if (
          columnInfo.data_type === DatasetDataType.TYPE_DATE ||
          columnInfo.data_type === DatasetDataType.TYPE_DATETIME ||
          columnInfo.data_type === DatasetDataType.TYPE_TIME
        ) {
          label = translateToUserTimezone(
            timezone,
            columnInfo.data_type,
            label?.toString() || "null"
          );
        } else if (typeof label === "string") {
          label = capitalizeEachWord(label?.toString() || "null", "_");
        } else if (typeof label === "number") {
          label = label.toLocaleString();
        }
        const obj: any = { name: label };
        valueIndexes.forEach((idx) => {
          const colName = summaryData.columns[idx].name;
          obj[colName] = row[idx] ?? null;
        });

        return obj;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [summaryData, timezone]);

  return { columns, rows, donutChartData, barChartData };
}

export default useAnalysisSummaryVisualization;
